import { Unstable_Popup as BasePopup } from "@mui/base/Unstable_Popup";
import { EmojiItem } from "@tiptap-pro/extension-emoji";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { EmojiListProps } from "../types";
import { Surface } from "@/components/FullPageEditor/components/ui/Surface";

const EmojiList = (
    {
        ...props
    }: EmojiListProps & {
        ref?: React.RefObject<unknown>;
    },
    ref
) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    // biome-ignore lint/correctness/useExhaustiveDependencies: no other way to reset the selected to 0 other than from useEffect here
    useEffect(() => {
        setSelectedIndex(0);
    }, [props.items]);

    const selectItem = useCallback(
        (index: number) => {
            const item = props.items[index];

            if (item) {
                props.command({ name: item.name });
            }
        },
        [props.command, props.items]
    );

    useImperativeHandle(
        ref,
        () => {
            const scrollIntoView = (index: number) => {
                const item = props.items[index];

                if (item) {
                    const node = document.querySelector(`[data-emoji-name="${item.name}"]`);

                    if (node) {
                        node.scrollIntoView({ block: "nearest" });
                    }
                }
            };

            const upHandler = () => {
                const newIndex = (selectedIndex + props.items.length - 1) % props.items.length;
                setSelectedIndex(newIndex);
                scrollIntoView(newIndex);
            };

            const downHandler = () => {
                const newIndex = (selectedIndex + 1) % props.items.length;
                setSelectedIndex(newIndex);
                scrollIntoView(newIndex);
            };

            const enterHandler = () => {
                selectItem(selectedIndex);
            };

            return {
                onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
                    if (event.key === "ArrowUp") {
                        upHandler();
                        return true;
                    }

                    if (event.key === "ArrowDown") {
                        downHandler();
                        return true;
                    }

                    if (event.key === "Enter") {
                        enterHandler();
                        return true;
                    }

                    return false;
                },
            };
        },
        [selectedIndex, selectItem, props.items]
    );

    const createClickHandler = useCallback((index: number) => () => selectItem(index), [selectItem]);

    if (!props.items || !props.items.length) {
        return null;
    }

    return (
        <BasePopup open={true} anchor={props.decorationNode} placement={"bottom-start"}>
            <Surface
                style={{
                    overflowY: "auto",
                    overflowX: "hidden",
                    padding: "0.8rem",
                    maxWidth: "28rem",
                    maxHeight: "28rem",
                }}>
                {props.items.map((item: EmojiItem, index: number) => (
                    <Button
                        active={index === selectedIndex}
                        variant="ghost"
                        style={{ justifyContent: "flex-start", width: "100%" }}
                        buttonSize="small"
                        key={item.name}
                        onClick={createClickHandler(index)}
                        data-emoji-name={item.name}
                        data-testid="auto-id-b8cbc914-01b8-4bc3-9af5-0197a859681a">
                        {item.fallbackImage ? (
                            // biome-ignore lint/nursery/noImgElement: <explanation>
                            (<img
                                src={item.fallbackImage}
                                style={{ width: "20px", height: "20px" }}
                                alt="emoji"
                                data-testid="auto-id-deae87bd-38b5-4e7c-97ac-ec020c67686c" />)
                        ) : (
                            item.emoji
                        )}{" "}
                        <span
                            className="truncate text-ellipsis"
                            data-testid="auto-id-4a62a091-0888-4861-93f0-b8bafe6ef63d">:{item.name}:</span>
                    </Button>
                ))}
            </Surface>
        </BasePopup>
    );
};

export default forwardRef(EmojiList);
